(function () {
  'use strict';

  /*
    Kit builder.

    Every string it writes was built by Liquid and parked on an element, so no
    wording — and no plural rule — lives in here. This file only picks which
    finished string to move where, and does the arithmetic Liquid cannot: the
    running total, which depends on a selection Liquid never sees.
  */

class ProductKit extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.patternVariantId = Number(this.dataset.patternVariantId);
    this.patternPrice = Number(this.dataset.patternPrice) || 0;
    this.patternAvailable = this.dataset.patternAvailable === 'true';
    this.pendingRequest = null;
    this.stockBlocked = !this.patternAvailable;
    this.currentAddLabel = this.dataset.textAdd || '';

    // Left empty rather than defaulted to a literal token string. A doubled
    // curly brace in this file reads as Liquid to theme-check.
    this.moneyFormat = this.dataset.moneyFormat || '';

    // The server checks the designer's colour; if that one is sold out, open on
    // the first that is not rather than on a dead selection.
    this.querySelectorAll('[data-kit-yarn]').forEach((card) => {
      const checked = card.querySelector('[data-kit-colour]:checked');
      if (!checked || !checked.hasAttribute('data-unavailable')) return;
      const available = card.querySelector('[data-kit-colour]:not([data-unavailable])');
      if (available) available.checked = true;
    });

    this.addEventListener('change', () => this.update());

    const add = this.querySelector('[data-kit-add]');
    if (add) add.addEventListener('click', () => this.addKit());

    const patternOnly = this.querySelector('[data-kit-pattern-only]');
    if (patternOnly) patternOnly.addEventListener('click', () => this.addPatternOnly());

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-kit-change]');
      if (trigger) {
        const card = trigger.closest('[data-kit-yarn]');
        const modal = card && card.querySelector('[data-kit-modal]');
        if (modal) this.openModal(modal);
        return;
      }

      const confirm = event.target.closest('[data-kit-confirm]');
      if (confirm) {
        const modal = confirm.closest('[data-kit-modal]');
        if (modal) this.closeModal(modal);
        return;
      }

      const chip = event.target.closest('[data-kit-family]');
      if (chip) this.filterFamily(chip);
    });

    this.querySelectorAll('[data-kit-modal]').forEach((modal) => {
      modal.addEventListener('click', (event) => {
        // The dialog's own box is the backdrop; anything inside it is content.
        if (event.target === modal) this.closeModal(modal);
      });

      // Explicit as well as native: three lines, and the picker stops depending
      // on the browser's own close-request path.
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeModal(modal);
      });
    });

    /*
      bfcache restores the page with `open` still set but without the dialog's
      place in the top layer, so a restored picker would paint inside its card.
    */
    this.onPageShow = () => {
      this.querySelectorAll('[data-kit-modal]').forEach((modal) => {
        if (modal.open) this.closeModal(modal);
      });
    };
    window.addEventListener('pageshow', this.onPageShow);

    this.onCartFinished = (event) => {
      if (!this.pendingRequest || event.detail?.requestId !== this.pendingRequest) return;
      this.pendingRequest = null;
      this.renderActions();
    };
    document.addEventListener('cart:added', this.onCartFinished);
    document.addEventListener('cart:error', this.onCartFinished);

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('cart:added', this.onCartFinished);
    document.removeEventListener('cart:error', this.onCartFinished);
  }

  /* ── Selection ───────────────────────────────────────────────────────── */

  get sizeKey() {
    const checked = this.querySelector('[data-kit-size]:checked');
    return checked ? checked.value : null;
  }

  get sizeLabel() {
    const checked = this.querySelector('[data-kit-size]:checked');
    if (!checked) return '';
    const label = this.querySelector('label[for="' + checked.id + '"]');
    return label ? label.textContent.trim() : checked.value;
  }

  /** Reads `size_key:skeins` pairs off the card, written once by Liquid. */
  skeinsFor(card, sizeKey) {
    let found = 0;
    String(card.dataset.skeinsBySize || '')
      .split(',')
      .forEach((pair) => {
        const parts = pair.split(':');
        if (parts.length === 2 && parts[0].trim() === sizeKey) {
          found = Number(parts[1]) || 0;
        }
      });
    return found;
  }

  /* ── Rendering ───────────────────────────────────────────────────────── */

  update() {
    const sizeKey = this.sizeKey;
    const sizeLabel = this.sizeLabel;

    let total = this.patternPrice;
    let totalSkeins = 0;
    let activeYarns = 0;
    let blocked = !this.patternAvailable;
    let anyOut = !this.patternAvailable;
    let anyLow = false;
    let blockingMessage = this.patternAvailable ? '' : this.dataset.textPatternUnavailable || '';

    this.querySelectorAll('[data-kit-yarn]').forEach((card) => {
      const skeins = this.skeinsFor(card, sizeKey);

      // A yarn this size does not call for drops out of the page entirely.
      card.hidden = skeins === 0;
      if (skeins === 0) return;

      activeYarns += 1;
      totalSkeins += skeins;

      const colour = card.querySelector('[data-kit-colour]:checked');

      // Two pieces so CSS can put the interpuncts in, which is what lets a slot
      // with no role label avoid a stranded separator.
      const need = card.querySelector('[data-kit-need]');
      if (need) {
        const template =
          skeins === 1 ? this.dataset.textTotalSkeinsOne : this.dataset.textTotalSkeinsOther;
        need.textContent = String(template || '').replace('__count__', String(skeins));
      }

      const needSize = card.querySelector('[data-kit-need-size]');
      if (needSize) {
        needSize.textContent = String(this.dataset.textRowSize || '').replace('__size__', sizeLabel);
      }

      const name = card.querySelector('[data-kit-colour-name]');
      if (name) name.textContent = colour ? colour.dataset.name || '' : '';

      this.paintSwatch(card.querySelector('[data-kit-preview]'), colour);
      this.paintSwatch(card.querySelector('[data-kit-modal-preview]'), colour);

      const chosen = card.querySelector('[data-kit-modal-selected]');
      if (chosen) {
        chosen.textContent = colour
          ? String(this.dataset.textSelected).replace('__name__', colour.dataset.name || '')
          : '';
      }

      // Wording and any count were built by Liquid and parked on the radio, so
      // switching colour only moves a finished string.
      const tracked = colour?.dataset.inventoryTracked === 'true';
      const inventoryQuantity = Number(colour?.dataset.inventoryQuantity) || 0;
      const continueSelling = colour?.dataset.inventoryPolicy === 'continue';
      const insufficient = Boolean(colour && tracked && !continueSelling && inventoryQuantity < skeins);

      const stock = card.querySelector('[data-kit-stock]');
      if (stock) {
        let label = colour ? colour.dataset.stockLabel || '' : '';
        if (insufficient) {
          label = String(this.dataset.textInsufficientStock || '')
            .replace('__needed__', String(skeins))
            .replace('__available__', String(Math.max(inventoryQuantity, 0)));
        }
        stock.textContent = label;
        stock.hidden = label === '';
        stock.dataset.state = insufficient ? 'insufficient' : colour ? colour.dataset.stockState || '' : '';
        if (insufficient && !blockingMessage) blockingMessage = label;
      }

      if (!colour || colour.hasAttribute('data-unavailable') || insufficient) {
        blocked = true;
        anyOut = true;
        if (!blockingMessage && colour) blockingMessage = colour.dataset.stockLabel || '';
        return;
      }
      if (colour.dataset.stockState === 'low_stock' || colour.dataset.stockState === 'backorder') {
        anyLow = true;
      }

      total += (Number(colour.dataset.price) || 0) * skeins;
    });

    this.writeCount('[data-kit-subtitle]', activeYarns, 'textSubtitleOne', 'textSubtitleOther');
    this.writeCount('[data-kit-total-skeins]', totalSkeins, 'textTotalSkeinsOne', 'textTotalSkeinsOther');
    this.writeCount('[data-kit-total-yarns]', activeYarns, 'textTotalYarnsOne', 'textTotalYarnsOther');

    const totalEl = this.querySelector('[data-kit-total]');
    if (totalEl) totalEl.textContent = blocked ? '' : this.money(total);

    const overall = this.querySelector('[data-kit-overall]');
    if (overall) {
      const label = anyOut
        ? overall.dataset.textOut
        : anyLow
          ? overall.dataset.textLow
          : overall.dataset.textAll;
      overall.textContent = label || '';
      overall.hidden = !label || activeYarns === 0;
      overall.dataset.state = anyOut ? 'out' : anyLow ? 'low' : 'all';
    }

    const label = this.querySelector('[data-kit-add-label]');
    this.currentAddLabel = blocked
      ? this.dataset.textAdd
      : this.dataset.textAdd + ' · ' + this.money(total);

    const notice = this.querySelector('[data-kit-notice]');
    if (notice) {
      notice.textContent = blockingMessage;
      notice.hidden = blockingMessage === '';
    }

    this.stockBlocked = blocked;
    this.renderActions();
  }

  renderActions() {
    const pending = Boolean(this.pendingRequest);
    const add = this.querySelector('[data-kit-add]');
    if (add) {
      add.setAttribute('aria-disabled', String(this.stockBlocked || pending));
      add.setAttribute('aria-busy', String(pending));
    }

    const label = this.querySelector('[data-kit-add-label]');
    if (label) label.textContent = pending ? this.dataset.textAdding || '' : this.currentAddLabel;

    const patternOnly = this.querySelector('[data-kit-pattern-only]');
    if (patternOnly) {
      patternOnly.setAttribute('aria-disabled', String(!this.patternAvailable || pending));
      patternOnly.setAttribute('aria-busy', String(pending));
    }
  }

  writeCount(selector, count, oneKey, otherKey) {
    this.querySelectorAll(selector).forEach((el) => {
      const template = count === 1 ? this.dataset[oneKey] : this.dataset[otherKey];
      el.textContent = String(template || '').replace('__count__', String(count));
    });
  }

  /** Repaints a preview dot from the chosen radio's own swatch attributes. */
  paintSwatch(el, colour) {
    if (!el) return;
    const image = colour ? colour.dataset.swatchImage : '';
    const solid = colour ? colour.dataset.swatchColor : '';
    el.style.backgroundImage = image ? 'url(' + image + ')' : '';
    el.style.backgroundColor = image ? '' : solid || '';
  }

  /**
   * Shopify's own money_format tokens. Formatting here rather than with `Intl`
   * keeps whatever the merchant configured, symbol and separators included,
   * instead of guessing from a currency code.
   */
  money(cents) {
    const value = (Number(cents) || 0) / 100;

    const group = (decimals, thousands, decimal) => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };

    if (!this.moneyFormat) return group(2, ',', '.');

    const token = new RegExp('\\{\\{\\s*(\\w+)\\s*\\}\\}', 'g');
    return this.moneyFormat.replace(token, (match, name) => {
      switch (name) {
        case 'amount':
          return group(2, ',', '.');
        case 'amount_no_decimals':
          return group(0, ',', '.');
        case 'amount_with_comma_separator':
          return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return group(0, ' ', '');
        case 'amount_with_space_separator':
          return group(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return group(2, ' ', '.');
        default:
          return match;
      }
    });
  }

  /* ── Colour picker ───────────────────────────────────────────────────── */

  openModal(modal) {
    if (modal.open || typeof modal.showModal !== 'function') return;
    modal.showModal();

    // Land the keyboard on the colour already chosen, not on the close button.
    const checked = modal.querySelector('[data-kit-colour]:checked');
    if (checked) checked.focus();
  }

  /*
    No scroll lock to manage: the dialog carries the theme's `scroll-lock`
    attribute and critical.css handles it with
    `html:has(dialog[scroll-lock][open])`. Pure CSS, so it cannot get stuck on.
  */
  closeModal(modal) {
    modal.close();
  }

  /**
   * Filters by Shopify Base colour family. Hiding rather than removing keeps
   * the radios in the form, so a colour filtered out of view is still the one
   * selected — a shopper who narrows to "Cool" does not silently lose their
   * warm choice.
   */
  filterFamily(chip) {
    const modal = chip.closest('[data-kit-modal]');
    if (!modal) return;

    const family = chip.dataset.kitFamily || '';

    modal.querySelectorAll('[data-kit-family]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === chip));
    });

    modal.querySelectorAll('.pdp-kit__colour').forEach((item) => {
      const families = ' ' + (item.dataset.families || '') + ' ';
      item.hidden = family !== '' && families.indexOf(' ' + family + ' ') === -1;
    });
  }

  /* ── Cart ────────────────────────────────────────────────────────────── */

  /**
   * The pattern and its yarn go in as separate lines, tagged with one `_kit`
   * token. An underscore keeps a property out of the customer's view, so the
   * token groups the lines without showing them a machine string.
   */
  items() {
    const kitId = 'kit-' + Date.now();
    const pattern = { id: this.patternVariantId, quantity: 1, properties: { _kit: kitId } };
    pattern.properties[this.dataset.textSizeProperty] = this.sizeLabel;

    const items = [pattern];

    this.querySelectorAll('[data-kit-yarn]').forEach((card) => {
      if (card.hidden) return;
      const skeins = this.skeinsFor(card, this.sizeKey);
      const colour = card.querySelector('[data-kit-colour]:checked');
      if (!skeins || !colour || colour.hasAttribute('data-unavailable')) return;

      items.push({
        id: Number(colour.dataset.variantId),
        quantity: skeins,
        properties: { _kit: kitId }
      });
    });

    return items;
  }

  addKit() {
    const add = this.querySelector('[data-kit-add]');
    if (add && add.getAttribute('aria-disabled') === 'true') return;
    this.send(this.items());
  }

  addPatternOnly() {
    if (!this.patternAvailable) return;
    this.send([{ id: this.patternVariantId, quantity: 1 }]);
  }

  /* assets/cart.js owns the request and the drawer; this only says what to add. */
  send(items) {
    if (!items.length || this.pendingRequest) return;
    if (items.some((item) => !Number.isFinite(Number(item.id)) || Number(item.id) <= 0)) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.add !== 'function') return;

    this.pendingRequest = 'kit-cart-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    this.renderActions();
    drawer.add({ items: items, requestId: this.pendingRequest });
  }
}

if (!customElements.get('product-kit')) {
  customElements.define('product-kit', ProductKit);
}

})();
